"use client";
import { toString } from "es-toolkit/compat";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { inferParserType, parseAsArrayOf, parseAsBoolean, parseAsString, useQueryStates } from "nuqs";
import { createContext, useCallback, useEffect } from "react";
import useUIState from "hooks/useUIState";
import { SortItem } from "types/baTypes";
import {
  CELL_SIDE_PARAMS_SET,
  COLLAPSIBLE_VIEWER_PARAMS_SET,
  CUSTOM_SEARCH_FILTERS_PARAMS_SET,
  DEAL_QUICK_EXPAND_PARAMS_SET,
  FILTERS_PARAMS_SET,
  FORM_PARAMS_SET,
  RecordItem,
  SIDEBAR_PARAMS_SET
} from "types/common";
import { SidebarContainer } from "utils/constants";
import { APP_QUERY_PARAM_TYPES, getCustomSearchFilterParams, getFilterParams } from "utils/paramsUtils";

export interface SetQueryParamsActionPayload {
  paramType: APP_QUERY_PARAM_TYPES;
  filters?: FILTERS_PARAMS_SET[];
  sortItems?: SortItem[];
  customSearchFilters?: CUSTOM_SEARCH_FILTERS_PARAMS_SET[];
  expanded?: boolean;
  sidebar?: SIDEBAR_PARAMS_SET;
  form?: FORM_PARAMS_SET;
  searchQuery?: string;
  cellSide?: CELL_SIDE_PARAMS_SET;
  viewSection?: {
    sectionId: string;
    sectionSlug: string;
  };
  collapsibleViewer?: COLLAPSIBLE_VIEWER_PARAMS_SET;
  dealQuickExpand?: DEAL_QUICK_EXPAND_PARAMS_SET;
}

interface RemoveQueryParamsActionPayload {
  paramsToRemove: APP_QUERY_PARAM_TYPES[];
}

const QUERY_PARAMS_INITIAL_STATE = {
  // for APP_QUERY_PARAM_TYPES.FILTERS
  filters: parseAsArrayOf(parseAsString, ";"),
  filters_slug: parseAsString,
  // for APP_QUERY_PARAM_TYPES.SORT_ITEMS
  sort: parseAsArrayOf(parseAsString, ";"),
  sort_slug: parseAsString,
  // for APP_QUERY_PARAM_TYPES.CUSTOM_SEARCH_FILTERS
  custom_search_filters: parseAsArrayOf(parseAsString, ";"),
  custom_search_filters_slug: parseAsString,
  // for APP_QUERY_PARAM_TYPES.SIDEBAR
  side_page: parseAsString,
  side_record: parseAsString,
  side_file: parseAsString,
  // for APP_QUERY_PARAM_TYPES.FORM
  form_id: parseAsString,
  form_page_form_id: parseAsString,
  form_page_id: parseAsString,
  form_path: parseAsString,
  form_sidebar: parseAsString,
  form_action: parseAsString,
  form_add: parseAsString,
  form_add_tab: parseAsString,
  form_parentid: parseAsString,
  form_expanded: parseAsBoolean,
  form_parent_page_id: parseAsString,
  form_title: parseAsString,
  form_trigger: parseAsString,
  // for APP_QUERY_PARAM_TYPES.VIEW_SECTION
  view_section: parseAsString,
  view_sectionslug: parseAsString,
  // for APP_QUERY_PARAM_TYPES.EXPANDED
  expanded: parseAsBoolean,
  // for APP_QUERY_PARAM_TYPES.CELL_SIDE
  cell_sidepageid: parseAsString,
  cell_siderecordid: parseAsString,
  cell_sidefile: parseAsString,
  cell_tabid: parseAsString,
  cell_sidetype: parseAsString,
  cell_sidetitle: parseAsString,
  // for APP_QUERY_PARAM_TYPES.COLLAPSIBLE_VIEWER
  collapsible_viewer_remove_item: parseAsString,
  // for APP_QUERY_PARAM_TYPES.SEARCH_QUERY
  search: parseAsString,
  // for APP_QUERY_PARAM_TYPES.DEAL_QUICK_EXPAND
  deal_quick_expand_record: parseAsString,
  deal_quick_expand_tab_path: parseAsString,
  deal_quick_expand_tab: parseAsString
};

export type APP_QUERY_PARAMS = inferParserType<typeof QUERY_PARAMS_INITIAL_STATE>;

export interface QueryParamsContextState {
  queryParams: APP_QUERY_PARAMS;
  queryParamsStr: string;
  clearParams: (paramsToRemove: APP_QUERY_PARAM_TYPES[]) => void;
  setParams: (payload: SetQueryParamsActionPayload) => void;
  pushRoute: (path: string) => void;
}

export const QueryParamsContext = createContext<QueryParamsContextState | null>(null);

const { Provider } = QueryParamsContext;

const getAppQueryParamsToUpdate = (payload: SetQueryParamsActionPayload): Partial<APP_QUERY_PARAMS> => {
  switch (payload.paramType) {
    case APP_QUERY_PARAM_TYPES.FILTERS:
      if (payload.filters?.length) {
        let filterParams: RecordItem = {};
        let filterSlug = "";
        payload.filters?.forEach((filter: FILTERS_PARAMS_SET, index: number) => {
          if (filter.filterOrGroup?.length) {
            const filterOrGroupParams: RecordItem = {};
            const orLabel = `f_or${filter.filterGroupType || index}`;
            filterOrGroupParams[orLabel] = "(";
            filter.filterOrGroup.forEach((filterOrGroupItem: FILTERS_PARAMS_SET) => {
              const flterParamsSet = getFilterParams(filterOrGroupItem);
              const filterKey = Object.keys(flterParamsSet || {})[0];
              filterOrGroupParams[orLabel] += `${filterKey}.${flterParamsSet[filterKey]}|`;
            });
            filterOrGroupParams[orLabel] += ")";
            filterParams = { ...filterParams, ...filterOrGroupParams };
          } else {
            const filterParamSet = getFilterParams(filter);

            filterParams = { ...filterParams, ...filterParamSet };
          }

          if (index === 0) {
            filterSlug = toString(filter.filterSlug);
          }
        });
        return {
          filters_slug: filterSlug,
          filters: Object.keys(filterParams || []).map((key) => `${key}=${filterParams[key]}`)
        };
      }
      break;
    case APP_QUERY_PARAM_TYPES.SORT:
      if (payload.sortItems?.length) {
        const sortValuesMap = payload.sortItems?.reduce((acc: RecordItem, sortItem) => {
          acc[`${sortItem.id}`] = sortItem.desc ? "desc" : "asc";
          return acc;
        }, {});
        return {
          sort: Object.keys(sortValuesMap || []).map((key) => `${key}:${sortValuesMap[key]}`),
          sort_slug: payload.sortItems?.[0].sortSlug
        };
      }
      break;
    case APP_QUERY_PARAM_TYPES.CUSTOM_SEARCH_FILTERS:
      if (payload.customSearchFilters?.length) {
        let filterParams: RecordItem = {};
        let filterSlug = "";
        payload.customSearchFilters?.forEach((filter: CUSTOM_SEARCH_FILTERS_PARAMS_SET, index: number) => {
          const filterParamSet = getCustomSearchFilterParams(filter);
          filterParams = { ...filterParams, ...filterParamSet };

          if (index === 0) {
            filterSlug = toString(filter.filterSlug);
          }
        });

        return {
          custom_search_filters_slug: filterSlug,
          custom_search_filters: Object.keys(filterParams || []).map((key) => `${key}=${filterParams[key]}`)
        };
      }
      break;
    case APP_QUERY_PARAM_TYPES.SIDEBAR:
      const { sidebar } = payload;
      if (sidebar?.pageId) {
        return {
          side_page: toString(sidebar.pageId),
          side_record: toString(sidebar.recordId),
          side_file: sidebar.fileColId ? toString(sidebar.fileColId) : null
        };
      }
      break;
    case APP_QUERY_PARAM_TYPES.EXPANDED:
      if (payload.expanded) {
        return {
          expanded: true
        };
      }
      break;
    case APP_QUERY_PARAM_TYPES.VIEW_SECTION:
      const { viewSection } = payload;
      if (viewSection?.sectionId) {
        return {
          view_section: toString(viewSection.sectionId),
          view_sectionslug: toString(viewSection.sectionSlug)
        };
      }
      break;
    case APP_QUERY_PARAM_TYPES.CELL_SIDEBAR:
      const { cellSide } = payload;
      if (cellSide?.pageId) {
        const cellSideParams: Partial<APP_QUERY_PARAMS> = {
          cell_sidepageid: toString(cellSide.pageId),
          cell_siderecordid: toString(cellSide.recordId)
        };
        if (cellSide?.tabId) {
          cellSideParams.cell_tabid = toString(cellSide.tabId);
        }
        if (cellSide.fileColId) {
          cellSideParams.cell_sidefile = toString(cellSide.fileColId);
        }

        if (cellSide?.type === SidebarContainer.Collapsible) {
          cellSideParams.cell_sidetype = cellSide.type;
        }

        if (cellSide?.title) {
          cellSideParams.cell_sidetitle = cellSide.title;
        }
        return cellSideParams;
      }
      break;
    case APP_QUERY_PARAM_TYPES.COLLAPSIBLE_VIEWER:
      const { collapsibleViewer } = payload;
      if (collapsibleViewer?.removeViewerId) {
        return {
          collapsible_viewer_remove_item: toString(collapsibleViewer.removeViewerId)
        };
      }
      break;
    case APP_QUERY_PARAM_TYPES.DEAL_QUICK_EXPAND:
      const { dealQuickExpand } = payload;
      const dealQuickExpandParams: Partial<APP_QUERY_PARAMS> = {};
      if (dealQuickExpand?.recordId) {
        dealQuickExpandParams.deal_quick_expand_record = toString(dealQuickExpand.recordId);
      }
      if (dealQuickExpand?.activeTabSlug) {
        dealQuickExpandParams.deal_quick_expand_tab = toString(dealQuickExpand.activeTabId);
        dealQuickExpandParams.deal_quick_expand_tab_path = toString(dealQuickExpand.activeTabSlug);
      }
      return dealQuickExpandParams;
    case APP_QUERY_PARAM_TYPES.SEARCH_QUERY:
      if (payload.searchQuery) {
        return {
          search: toString(payload.searchQuery)
        };
      } else {
        return {
          search: null
        };
      }
    case APP_QUERY_PARAM_TYPES.FORM:
      const { form } = payload;
      if (form?.tablePath) {
        const formParams: Partial<APP_QUERY_PARAMS> = {
          form_path: toString(form.tablePath)
        };
        if (form.action) {
          formParams.form_action = toString(form.action);
        }
        if (form.formId) {
          formParams.form_id = toString(form.formId);
        }
        if (form.pageFormId) {
          formParams.form_page_form_id = toString(form.pageFormId);
        }
        if (form.pageId) {
          formParams.form_page_id = toString(form.pageId);
        }
        if (form?.parentRecordId) {
          formParams.form_parentid = toString(form.parentRecordId);
        }
        if (form?.parentRecordPageId) {
          formParams.form_parent_page_id = toString(form.parentRecordPageId);
        }
        if (form.add?.addPageId) {
          formParams.form_add = toString(form.add?.addPageId);

          if (form.add?.expanded) {
            formParams.form_expanded = true;
          }

          if (form.add?.tabId) {
            formParams.form_add_tab = toString(form.add?.tabId);
          }
        }
        if (form?.sidebar) {
          formParams.form_sidebar = toString(form.sidebar);
        }

        if (form?.title) {
          formParams.form_title = toString(form.title);
        }

        if (form.trigger) {
          formParams.form_trigger = form.trigger;
        }
        return formParams;
      }
      break;
    default:
      return {};
  }
  return {};
};

const getAppQueryParamsToRemove = (payload: RemoveQueryParamsActionPayload): Partial<APP_QUERY_PARAMS> => {
  const queryParamsToRemove: Partial<APP_QUERY_PARAMS> = {};
  payload.paramsToRemove.forEach((paramType) => {
    switch (paramType) {
      case APP_QUERY_PARAM_TYPES.FILTERS:
        queryParamsToRemove["filters"] = null;
        queryParamsToRemove["filters_slug"] = null;
        break;
      case APP_QUERY_PARAM_TYPES.SORT:
        queryParamsToRemove["sort"] = null;
        queryParamsToRemove["sort_slug"] = null;
        break;
      case APP_QUERY_PARAM_TYPES.CUSTOM_SEARCH_FILTERS:
        queryParamsToRemove["custom_search_filters"] = null;
        queryParamsToRemove["custom_search_filters_slug"] = null;
        break;
      case APP_QUERY_PARAM_TYPES.SEARCH_QUERY:
        queryParamsToRemove["search"] = null;
        break;
      case APP_QUERY_PARAM_TYPES.SIDEBAR:
        queryParamsToRemove["side_page"] = null;
        queryParamsToRemove["side_record"] = null;
        queryParamsToRemove["side_file"] = null;
        break;
      case APP_QUERY_PARAM_TYPES.FORM:
        queryParamsToRemove["form_id"] = null;
        queryParamsToRemove["form_page_form_id"] = null;
        queryParamsToRemove["form_page_id"] = null;
        queryParamsToRemove["form_path"] = null;
        queryParamsToRemove["form_sidebar"] = null;
        queryParamsToRemove["form_action"] = null;
        queryParamsToRemove["form_add"] = null;
        queryParamsToRemove["form_add_tab"] = null;
        queryParamsToRemove["form_parentid"] = null;
        queryParamsToRemove["form_expanded"] = null;
        queryParamsToRemove["form_parent_page_id"] = null;
        queryParamsToRemove["form_title"] = null;
        queryParamsToRemove["form_trigger"] = null;
        break;
      case APP_QUERY_PARAM_TYPES.VIEW_SECTION:
        queryParamsToRemove["view_section"] = null;
        queryParamsToRemove["view_sectionslug"] = null;
        break;
      case APP_QUERY_PARAM_TYPES.EXPANDED:
        queryParamsToRemove["expanded"] = null;
        break;
      case APP_QUERY_PARAM_TYPES.CELL_SIDEBAR:
        queryParamsToRemove["cell_sidepageid"] = null;
        queryParamsToRemove["cell_siderecordid"] = null;
        queryParamsToRemove["cell_sidefile"] = null;
        queryParamsToRemove["cell_tabid"] = null;
        queryParamsToRemove["cell_sidetype"] = null;
        queryParamsToRemove["cell_sidetitle"] = null;
        break;
      case APP_QUERY_PARAM_TYPES.COLLAPSIBLE_VIEWER:
        queryParamsToRemove["collapsible_viewer_remove_item"] = null;
        break;
      case APP_QUERY_PARAM_TYPES.DEAL_QUICK_EXPAND:
        queryParamsToRemove["deal_quick_expand_record"] = null;
        queryParamsToRemove["deal_quick_expand_tab_path"] = null;
        queryParamsToRemove["deal_quick_expand_tab"] = null;
        break;
      case APP_QUERY_PARAM_TYPES.DEAL_QUICK_EXPAND_TAB:
        queryParamsToRemove["deal_quick_expand_tab"] = null;
        queryParamsToRemove["deal_quick_expand_tab_path"] = null;
        break;
      default:
        break;
    }
  });
  return queryParamsToRemove;
};
export const QueryParamsContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [queryParams, setQueryParams] = useQueryStates(QUERY_PARAMS_INITIAL_STATE);
  const pathname = usePathname();
  const { updateUIState } = useUIState();
  const queryParamsFromNxt = useSearchParams();
  const router = useRouter();

  useEffect(() => {
    updateUIState({ routeChangeLoading: false });
  }, [pathname, updateUIState]);

  const clearParams = useCallback(
    (paramsToRemove: APP_QUERY_PARAM_TYPES[]) => {
      const queryParamsToRemove = getAppQueryParamsToRemove({
        paramsToRemove
      });
      setQueryParams(queryParamsToRemove);
    },
    [setQueryParams]
  );

  const setParams = useCallback(
    (payload: SetQueryParamsActionPayload) => {
      const queryParamsToUpdate = getAppQueryParamsToUpdate(payload);
      setQueryParams(queryParamsToUpdate);
    },
    [setQueryParams]
  );

  const pushRoute = useCallback(
    (path: string) => {
      updateUIState({ routeChangeLoading: true });
      router.push(path, { scroll: false });
    },
    [router, updateUIState]
  );

  return (
    <Provider
      value={{
        queryParams,
        clearParams,
        setParams,
        queryParamsStr: queryParamsFromNxt?.toString() || "",
        pushRoute
      }}
    >
      {children}
    </Provider>
  );
};
